import {
    eventsFromEmailText,
    removeFromCalendarText,
    reportAnIssueText,
} from '../../strings.locstring.json';
import loc from 'owa-localize';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';

import logActionDatapoint from '../telemetry/logActionDatapoint';
import TailoredActionName from 'owa-txp/lib/store/schema/telemetry/TailoredActionName';
import TxpConfiguration from 'txp-data/lib/schema/configSchema/TxpConfiguration';
import CardItem from 'txp-data/lib/schema/viewSchema/CardItem';
import { MAIL_LOCATION } from 'owa-txp/lib/helpers/TxpConstants';
import ActivityType from 'txp-data/lib/schema/innerSchema/ActivityType';
import TxpDatapoint from 'txp-data/lib/schema/configSchema/TxpDatapoint';
import convertEntityTypeToEventFlag from './convertEntityTypeToEventFlag';
import { getDialogTitleForTxpFlag, TxpEventFlagsV2 } from 'owa-txp-option/lib/utils/getTxpFlagInfo';
import { isFeatureEnabled } from 'owa-feature-flags';
import getEditableCustomData from '../telemetry/getEditableCustomData';
import shouldShowEditButton from './shouldShowEditButton';
import { editableFlagCustomData } from '../../helpers/EditableCardConsts';
import isEntityDisabledByServer from 'owa-txp-option/lib/utils/isEntityDisabledByServer';
import getTxpSettings from 'owa-calendar-configuration/lib/utils/getTxpSettings';
import { TxpGriffinSettingsPayload } from 'owa-calendar-configuration';
import ProcessorLevels from 'owa-txp-option/lib/store/schema/ProcessorLevels';
import isTxpDisabled from 'owa-txp-option/lib/utils/isTxpDisabled';

export default function getTxpContextMenuItems(
    txpConfiguration: TxpConfiguration,
    cardItem: CardItem,
    onStateChanged: (changed: boolean) => void
): IContextualMenuItem[] {
    let txpActionSubMenuItems: IContextualMenuItem[] = [];

    if (txpConfiguration) {
        if (shouldShowEditButton(txpConfiguration, cardItem) && cardItem.actionButtonData) {
            let { actionButtonText, actionName, actionUrl } = cardItem.actionButtonData;

            txpActionSubMenuItems.push({
                key: actionName,
                name: actionButtonText,
                onClick: () =>
                    onActionButtonClicked(
                        actionName,
                        txpConfiguration.datapointLogger,
                        cardItem.activityType,
                        actionUrl,
                        editableFlagCustomData
                    ),
            } as IContextualMenuItem);
        }

        if (txpConfiguration.onEventsFromEmailSettingsClicked) {
            let menuItemName: string;
            const eventType: string = convertEntityTypeToEventFlag(cardItem.entityType);
            const txpSettings: TxpGriffinSettingsPayload = getTxpSettings();
            const selectedValue: ProcessorLevels =
                txpSettings &&
                txpSettings.EntityTypeProcessorLevelSettings &&
                txpSettings.EntityTypeProcessorLevelSettings[eventType];
            const isOptionsV2Enabled: boolean = isFeatureEnabled('cal-txp-optionsV2');
            const isTxpEnabled: boolean =
                txpSettings && !isTxpDisabled(txpSettings.TxpDisableReason);
            const isEntityEnabled = !isEntityDisabledByServer(selectedValue);

            if (eventType && isOptionsV2Enabled && isTxpEnabled && isEntityEnabled) {
                menuItemName = getDialogTitleForTxpFlag(eventType as TxpEventFlagsV2);
            } else {
                menuItemName = loc(eventsFromEmailText);
            }

            if (!isOptionsV2Enabled || (isTxpEnabled && isEntityEnabled)) {
                txpActionSubMenuItems.push({
                    key: 'EventsFromEmail',
                    name: menuItemName,
                    onClick: () =>
                        onEventsFromEmailSettingsClickedHandler(txpConfiguration, cardItem),
                } as IContextualMenuItem);
            }
        }

        if (txpConfiguration.onRemoveFromCalendarClicked && cardItem.calendarEventId) {
            txpActionSubMenuItems.push({
                key: 'RemoveFromCalendar',
                name: loc(removeFromCalendarText),
                onClick: () =>
                    onRemoveFromCalendarClickedHandler(txpConfiguration, cardItem, onStateChanged),
            } as IContextualMenuItem);
        }

        if (txpConfiguration.onReportIssueClicked) {
            txpActionSubMenuItems.push({
                key: 'ReportAnIssue',
                name: loc(reportAnIssueText),
                onClick: () => onReportIssueClickedHandler(txpConfiguration),
            } as IContextualMenuItem);
        }
    }

    return txpActionSubMenuItems;
}

/**
 * Event handler passed to Report Issue context menu item
 */
function onReportIssueClickedHandler(txpConfiguration: TxpConfiguration) {
    txpConfiguration.onReportIssueClicked(TailoredActionName.ReportAnIssue, MAIL_LOCATION);
}

/**
 * Event handler passed to Events from Email Settings Context menu item
 */
function onEventsFromEmailSettingsClickedHandler(
    txpConfiguration: TxpConfiguration,
    cardItem: CardItem
) {
    txpConfiguration.onEventsFromEmailSettingsClicked(cardItem);

    logActionDatapoint(
        TailoredActionName.Settings,
        txpConfiguration.datapointLogger,
        cardItem.activityType
    );
}

/**
 * Event handler passed to Remove from calendar context menu item
 */
function onRemoveFromCalendarClickedHandler(
    txpConfiguration: TxpConfiguration,
    cardItem: CardItem,
    onStateChanged: (changed: boolean) => void
) {
    txpConfiguration.onRemoveFromCalendarClicked();
    const editableCustomData = getEditableCustomData(txpConfiguration, cardItem);

    logActionDatapoint(
        TailoredActionName.RemoveFromCalendar,
        txpConfiguration.datapointLogger,
        cardItem.activityType,
        editableCustomData
    );

    onStateChanged(true);
}

function onActionButtonClicked(
    actionName: string,
    datapointLogger: (txpDatapoint: TxpDatapoint, datapointName: string) => void,
    activityType: ActivityType,
    actionUrl: string,
    customData?: any
) {
    logActionDatapoint(actionName, datapointLogger, activityType, customData);
    window.open(actionUrl);
}
